import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { getCookie, removeCookie, setCookie } from '../../utils/cookies';
import { useConfig } from '../../Hooks/useConfig';
import { useAuthStatus } from '../../store/auth/useAuthStatus';

export const CustomizDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  overflow: 'hidden',
  width: '100%',
  top: 50,
  zIndex: 999,
  backgroundColor: theme.palette.secondary.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.secondary.neutral_30 : theme.palette.secondary.neutral_90}`,
  borderRadius: theme.spacing(5),
  minWidth: '250px',
  '.MuiListItemButton-root': {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.secondary.surface_22
          : theme.palette.primary.primary_99,
    },
  },
  [theme.breakpoints.down('md')]: {
    borderRadius: theme.spacing(5, 5, 0, 0),
    position: 'fixed',
    top: 'auto',
    bottom: '0',
    left: '0',
    right: '0',
  },
}));

const IconListImg = styled('img')(({ theme }) => ({
  width: theme.spacing(6),
  height: theme.spacing(6),
  objectFit: 'contain',
}));

const BgBlurContent = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  backgroundColor: 'rgba(27, 27, 31, 0.2)',
  backdropFilter: 'blur(2.5px) ',
  zIndex: 998,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const ListText = styled(ListItemText)(({ theme }) => ({
  '.MuiListItemText-primary': {
    fontSize: `${theme.spacing(3.5)} !important`,
    fontWeight: 600,
  },
}));

const Dropdown2 = ({
  selected = () => {
    return;
  },
  list,
  children,
  sx,
  rightItem = false,
  hasHeader = false,
  hasFooter = false,
}) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const history = useHistory();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { baseDomain } = useConfig();
  const { setToken } = useAuthStatus();

  const handleSelect = (option) => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const Logout = async () => {
    removeCookie('token', baseDomain);
    removeCookie('showFeedbackBanner', baseDomain);
    removeCookie('credit_warning_login_shown', baseDomain);
    removeCookie('credit_warning_playground_shown', baseDomain);

    let theme = getCookie('darkMode'); //localStorage.getItem("darkMode");
    localStorage.clear();
    setCookie('darkMode', theme, 34560000, baseDomain);
    setToken('');
    await history.push('/login');
  };

  return (
    <Box
      sx={{ position: smUp ? 'relative' : 'static', ...sx }}
      ref={dropdownRef}
    >
      {isOpen && (
        <BgBlurContent onClick={() => setIsOpen(!isOpen)}></BgBlurContent>
      )}
      <Box onClick={() => setIsOpen(!isOpen)}>{children}</Box>
      {isOpen && (
        <CustomizDropdown
          sx={{
            right: (theme) => (theme.direction !== 'rtl' ? 0 : 'auto'),
            left: (theme) => (theme.direction !== 'ltr' ? 0 : 'auto'),
          }}
        >
          <List sx={{ p: 0 }} subheader={hasHeader ? hasHeader : false}>
            {list.map((listItem, index) =>
              listItem.link && !listItem.link.includes('https://') ? (
                <Link key={index} to={listItem.link}>
                  <ListItem
                    sx={{ color: 'primary.contrastText' }}
                    key={listItem.icon}
                    disablePadding
                  >
                    <ListItemButton
                      sx={{ py: 3, px: 6 }}
                      onClick={listItem.logout ? Logout : handleSelect}
                    >
                      <Box sx={{ mr: 2 }}>
                        {listItem.type && listItem.type === 'image' ? (
                          <IconListImg
                            src={`/static/img/icon/${listItem.icon}`}
                          />
                        ) : (
                          <listItem.icon size={24} />
                        )}
                      </Box>
                      <ListText primary={t(listItem.text)} />
                    </ListItemButton>
                  </ListItem>
                </Link>
              ) : listItem.link && listItem.link.includes('https://') ? (
                <Typography component={'a'} key={index} href={listItem.link}>
                  <ListItem
                    sx={{ color: 'primary.contrastText' }}
                    key={listItem.icon}
                    disablePadding
                  >
                    <ListItemButton
                      sx={{ py: 3, px: 6 }}
                      onClick={listItem.logout ? Logout : handleSelect}
                    >
                      <Box sx={{ mr: 2 }}>
                        {listItem.type && listItem.type === 'image' ? (
                          <IconListImg
                            src={`/static/img/icon/${listItem.icon}`}
                          />
                        ) : (
                          <listItem.icon size={24} />
                        )}
                      </Box>
                      <ListText primary={t(listItem.text)} />
                    </ListItemButton>
                  </ListItem>
                </Typography>
              ) : (
                <ListItem
                  onClick={() => selected(listItem.value)}
                  key={index}
                  disablePadding
                >
                  <ListItemButton
                    sx={{ py: 3, px: 6 }}
                    onClick={listItem.logout ? Logout : handleSelect}
                  >
                    <Box sx={{ mr: 2 }}>
                      {listItem.type && listItem.type === 'image' ? (
                        <IconListImg
                          src={`/static/img/icon/${listItem.icon}`}
                        />
                      ) : (
                        <listItem.icon size={24} />
                      )}
                    </Box>
                    {listItem.text ? (
                      <ListText primary={t(listItem.text)} />
                    ) : (
                      <Skeleton sx={{ width: '100%' }} animation="wave" />
                    )}
                  </ListItemButton>
                </ListItem>
              )
            )}
          </List>
          {hasFooter ? hasFooter : null}
        </CustomizDropdown>
      )}
    </Box>
  );
};

export default Dropdown2;
